import { Component } from '@theme/component-base';

/**
 * StickyHeader
 * Handles sticky header behavior with scroll detection and dynamic show/hide
 */
class StickyHeader extends Component {
  constructor() {
    super();
    
    this.scrollThreshold = 50;
    this.scrollTolerance = 5;
    this.lastScrollY = 0;
    this.currentScrollY = 0;
    this.isScrolled = false;
    this.isHidden = false;
    this.headerElement = null;
    this.headerType = 'none';
    this.scrollTimeout = null;
    this.throttleDelay = 10;
    this.toolbarElement = null;
    this.toolbarHeight = 0;
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.initialized) return;
    this.initialized = true;

    this.headerElement = this.closest('header');
    if (!this.headerElement) {
      console.error('[StickyHeader] No parent <header> element found');
      return;
    }

    this.headerType = this.headerElement.dataset.headerType || 'none';

    if (this.headerType === 'none') {
      return;
    }

    this.currentScrollY = window.scrollY;
    this.lastScrollY = this.currentScrollY;

    // Check for sticky toolbar and calculate offset
    this.#calculateToolbarOffset();

    this.#addEventListeners();

    if (this.currentScrollY <= this.scrollThreshold && this.headerElement.hasAttribute('data-header-hidden')) {
      this.headerElement.removeAttribute('data-header-hidden');
      this.headerElement.style.transition = '';
    }

    this.#updateHeaderState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeEventListeners();
    
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
  }

  #addEventListeners() {
    window.addEventListener('scroll', this.#handleScroll, { passive: true });
    
    // Add hover listeners if remove transparency on hover is enabled
    if (this.headerElement.hasAttribute('data-remove-transparency-on-hover')) {
      this.headerElement.addEventListener('mouseenter', this.#handleMouseEnter);
      this.headerElement.addEventListener('mouseleave', this.#handleMouseLeave);
    }
  }

  #removeEventListeners() {
    window.removeEventListener('scroll', this.#handleScroll);
    
    if (this.headerElement.hasAttribute('data-remove-transparency-on-hover')) {
      this.headerElement.removeEventListener('mouseenter', this.#handleMouseEnter);
      this.headerElement.removeEventListener('mouseleave', this.#handleMouseLeave);
    }
  }

  #handleMouseEnter = () => {
    this.headerElement.classList.add('header-hovered');
  };

  #handleMouseLeave = () => {
    this.headerElement.classList.remove('header-hovered');
  };

  #handleScroll = () => {
    if (this.scrollTimeout) return;

    this.scrollTimeout = setTimeout(() => {
      this.currentScrollY = window.scrollY;
      this.#updateHeaderState();
      this.scrollTimeout = null;
    }, this.throttleDelay);
  };

  #updateHeaderState() {
    const scrollDelta = this.currentScrollY - this.lastScrollY;
    
    if (this.headerElement.hasAttribute('data-header-hidden')) {
      this.headerElement.removeAttribute('data-header-hidden');
      this.headerElement.style.transition = '';
    }
    
    this.#updateScrolledClass();
    
    if (this.headerType === 'scroll-up') {
      this.#updateScrollUpBehavior(scrollDelta);
    }
    
    this.lastScrollY = this.currentScrollY;
  }

  #updateScrolledClass() {
    const shouldBeScrolled = this.currentScrollY > this.scrollThreshold;
    
    if (shouldBeScrolled !== this.isScrolled) {
      this.isScrolled = shouldBeScrolled;
      
      if (this.isScrolled) {
        this.headerElement.classList.add('scrolled');
      } else {
        this.headerElement.classList.remove('scrolled');
      }
    }
  }

  #updateScrollUpBehavior(scrollDelta) {
    if (this.currentScrollY <= this.scrollThreshold) {
      if (this.isHidden) {
        this.#showHeader();
      }
      return;
    }
    
    if (this.headerElement.classList.contains('menu-open')) {
      if (this.isHidden) {
        this.#showHeader();
      }
      return;
    }
    
    if (scrollDelta > this.scrollTolerance) {
      if (!this.isHidden) {
        this.#hideHeader();
      }
    } else if (scrollDelta < -this.scrollTolerance) {
      if (this.isHidden) {
        this.#showHeader();
      }
    }
  }

  #hideHeader() {
    this.isHidden = true;
    this.headerElement.classList.add('header-hidden');
  }

  #showHeader() {
    this.isHidden = false;
    this.headerElement.classList.remove('header-hidden');
  }

  #calculateToolbarOffset() {
    // Find sticky toolbar (could be above or below header in header group)
    this.toolbarElement = document.querySelector('.toolbar-section');
    const headerGroupWrapper = this.headerElement.closest('.shopify-section-group-header-group');
    if (!this.toolbarElement) {
      this.toolbarHeight = 0;
      this.headerGroupWrapper.style.top = '0px';
      return;
    }


    // Check if toolbar has sticky positioning
    const toolbarStyles = window.getComputedStyle(this.toolbarElement);
    const isSticky = toolbarStyles.position === 'sticky' || toolbarStyles.position === 'fixed';
    
    if (!isSticky) {
      this.toolbarHeight = 0;
      headerGroupWrapper.style.top = '0px';
      return;
    }

    // Check if toolbar is above the header
    const toolbarRect = this.toolbarElement.getBoundingClientRect();
    const headerRect = headerGroupWrapper.getBoundingClientRect();
    const isToolbarAboveHeader = toolbarRect.top < headerRect.top;
    
    if (isToolbarAboveHeader) {
      // Toolbar is above header, set header top offset
      this.toolbarHeight = this.toolbarElement.offsetHeight;
      headerGroupWrapper.style.top = `${this.toolbarHeight}px`;
    } else {
      // Toolbar is below header, no offset needed
      this.toolbarHeight = 0;
      headerGroupWrapper.style.top = '0px';
    }
  }
}

function initStickyHeader() {
  if (!customElements.get('sticky-header')) {
    customElements.define('sticky-header', StickyHeader);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initStickyHeader);
} else {
  initStickyHeader();
}

export { StickyHeader, initStickyHeader };